// IVR numbers of both vendors (plan v2 §5.4). Exotel lists and sells numbers by API ("Sync from Exotel", "Buy a
// number"); Pingbix has no voice API yet, so its numbers are added by hand (reminder 74). The number's series (140
// promotional / 1600 service / normal) is read from its digits; a promotional campaign may call only from 140.
import { z } from 'zod'
import { Id, IsoDateTime } from './common'
import { NumberSeries, type PhoneNumber } from './numbers'

/** The telecom circles a number can belong to (the ones our vendors offer). */
export const REGIONS = [
  'All India',
  'Karnataka',
  'Maharashtra',
  'Mumbai',
  'Delhi',
  'Tamil Nadu',
  'Andhra Pradesh & Telangana',
  'West Bengal',
] as const
export const Region = z.enum(REGIONS)
export type Region = z.infer<typeof Region>

/** What each IVR vendor lets the portal do with numbers. */
export const IvrVendor = z.object({
  vendor_id: Id,
  name: z.string(),
  onboarded: z.boolean(),
  number_api: z.boolean().describe('the vendor lists our numbers by API (Sync); false = added by hand'),
  can_buy: z.boolean().describe('our account may buy numbers by API'),
  calls_on: z.boolean().describe("the vendor's call API is on: its numbers can place calls"),
  numbers: z.number().int().min(0),
  synced_at: IsoDateTime.nullable(),
  note: z.string(),
})
export type IvrVendor = z.infer<typeof IvrVendor>

export const SyncAnswer = z.object({
  added: z.number().int().min(0).describe('new at the vendor: now in the portal'),
  gone: z.number().int().min(0).describe('no longer at the vendor: marked gone'),
  unchanged: z.number().int().min(0),
})
export type SyncAnswer = z.infer<typeof SyncAnswer>

export const ForSaleFilter = z.object({ series: NumberSeries, region: Region.optional() })
export type ForSaleFilter = z.infer<typeof ForSaleFilter>

export const NumberForSale = z.object({
  display: z.string(),
  series: NumberSeries,
  region: Region,
  monthly_rent: z.number().int().min(0).describe('rupees a month, as the vendor quotes it'),
})
export type NumberForSale = z.infer<typeof NumberForSale>

/** A new IVR number starts with this calls-a-day limit (Exotel's default for one number). */
export const IVR_DAILY_DEFAULT = 1500

const CallsPerDay = z.number('Type a number.').int().min(1, 'At least 1 a day').max(20000)

export const BuyNumberInput = z.object({
  display: z.string().min(1, 'Pick a number'),
  name: z.string().trim().min(1, 'Give it a name'),
  daily_limit: CallsPerDay,
})
export type BuyNumberInput = z.infer<typeof BuyNumberInput>

/** Digits of an Indian number: 140xxxxxxx, 1600xxxxxx or a 10-digit number, with or without +91 and spaces. */
const IvrDigits = z
  .string()
  .trim()
  .refine((value) => /^\d{10}$/.test(value.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '')), {
    message: 'A 10-digit number, e.g. +91 140 447 2001',
  })

export const AddIvrNumberInput = z.object({
  vendor_id: Id,
  display: IvrDigits,
  name: z.string().trim().min(1, 'Give it a name'),
  region: Region,
  daily_limit: CallsPerDay,
})
export type AddIvrNumberInput = z.infer<typeof AddIvrNumberInput>

export interface IvrNumbersApi {
  listIvrVendors(): Promise<IvrVendor[]>
  /** reads the vendor's number list: new ones appear, gone ones are marked (Exotel only) */
  syncIvrNumbers(vendorId: string): Promise<SyncAnswer>
  listNumbersForSale(vendorId: string, filter: ForSaleFilter): Promise<NumberForSale[]>
  buyIvrNumber(vendorId: string, input: BuyNumberInput): Promise<PhoneNumber>
  /** for a vendor without a number API (Pingbix) */
  addIvrNumber(input: AddIvrNumberInput): Promise<PhoneNumber>
}
